/* ============================================================
   Runtime theming for multi-tenant deployment.
   Resolves the brand color (and optional background) and writes it
   to CSS variables (--brand, --brand-rgb) on :root, so the entire
   app re-themes from one source — no rebuild needed per customer.

   Priority:
     1. Remote per-tenant config  (config.theme.primary)   [runtime]
     2. Build-time env var         (REACT_APP_THEME_COLOR)  [per-build]
     3. Default copper             (#B87333)                [fallback]
   ============================================================ */

const DEFAULT_BRAND = "#B87333";

/* Last theme applied from a real /v1/config, per host, so a reload paints
   the tenant's colors straight away instead of copper until the config
   request resolves. public/index.html reads the same key synchronously
   before first paint — keep the two in sync. */
const THEME_CACHE_PREFIX = "tvid.theme.";
const THEME_VARS = ["--brand-rgb", "--brand", "--color-bg", "--font-color", "--radius-base", "--font-family"];

function themeCacheKey() {
  return THEME_CACHE_PREFIX + window.location.hostname;
}

function readThemeCache() {
  try {
    const cached = JSON.parse(window.localStorage.getItem(themeCacheKey()));
    return cached && cached.vars ? cached : null;
  } catch {
    return null;
  }
}

function writeThemeCache(font) {
  const style = document.documentElement.style;
  const vars = {};
  THEME_VARS.forEach((name) => {
    const value = style.getPropertyValue(name);
    if (value) vars[name] = value;
  });
  try {
    window.localStorage.setItem(themeCacheKey(), JSON.stringify({ vars, font: font || null }));
  } catch {
    // storage full/blocked — next reload just falls back to env/default
  }
}

/** Re-apply the cached tenant theme; returns false when there is none. */
function applyCachedTheme() {
  const cached = readThemeCache();
  if (!cached) return false;
  const root = document.documentElement;
  Object.entries(cached.vars).forEach(([name, value]) => root.style.setProperty(name, value));
  if (cached.vars["--font-color"]) root.setAttribute("data-font-color", "");
  if (cached.font) loadGoogleFont(cached.font);
  return true;
}

/** "#B87333" | "rgb(184,115,51)" -> "184, 115, 51" (or null if unparseable) */
function toRgbTriplet(input) {
  if (!input || typeof input !== "string") return null;
  const v = input.trim();

  // #RGB or #RRGGBB
  const hex = v.replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `${r}, ${g}, ${b}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }

  // rgb()/rgba()
  const m = v.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return `${m[1]}, ${m[2]}, ${m[3]}`;

  return null;
}

/**
 * Whether the browser can actually resolve `value` as a CSS color. Guards
 * every runtime var write below — the admin panel's "preset" swatches can
 * save a label like "midnight" instead of a hex code, and an unresolvable
 * value written to a CSS var breaks *any* declaration that later reads it
 * (background/border/etc. compute to their initial value, not the var()
 * fallback), which is why a bad remote value used to blank the whole page.
 */
function isValidCssColor(value) {
  if (!value || typeof value !== "string") return false;
  try {
    if (typeof CSS !== "undefined" && CSS.supports) {
      return CSS.supports("color", value);
    }
  } catch {
    // fall through to DOM probe below
  }
  const probe = document.createElement("span").style;
  probe.color = "";
  probe.color = value;
  return probe.color !== "";
}

/** Apply a brand color (any CSS color string) to the global CSS variables. */
export function applyBrandColor(colorString) {
  const root = document.documentElement;
  const triplet = toRgbTriplet(colorString);
  if (triplet) {
    // Preferred path: drive both --brand and rgba tints from one triplet
    root.style.setProperty("--brand-rgb", triplet);
  } else if (colorString && isValidCssColor(colorString)) {
    // Non-rgb color (e.g. named/hsl) — set --brand directly; tints keep default
    root.style.setProperty("--brand", colorString);
  } else if (colorString) {
    console.warn(`[theme] accent_color "${colorString}" isn't a valid CSS color — keeping previous brand color.`);
  }
}

/**
 * Load a Google Font family at runtime (only when the tenant asks for
 * something other than the bundled default), so config.font_family can
 * be an arbitrary web font name without a build-time dependency.
 */
function loadGoogleFont(family) {
  const id = `google-font-${family.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;

  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family
  ).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/** Raw per-tenant brand color from the v1/config payload (unvalidated), or null. */
export function resolveRemoteBrandColor(config) {
  return (
    config?.accent_color ||
    config?.theme?.primary ||
    config?.theme_color ||
    config?.App_setting?.[0]?.theme_color ||
    null
  );
}

/**
 * Apply theme from (optional) remote config, falling back to env, then default.
 * Call with no args at startup to apply env/default immediately.
 *
 * `config` is the payload of GET /v1/config (which already inlines every
 * field also returned by GET /v1/config/theme — accent_color,
 * app_background, corner_radius, font_family — so there's no need to
 * call the theme endpoint separately).
 */
export function applyTheme(config) {
  const root = document.documentElement;

  // Config not loaded yet (null) or the request failed ({}): keep the
  // tenant's last known theme rather than dropping back to copper.
  const loaded = !!config && Object.keys(config).length > 0;
  if (!loaded && applyCachedTheme()) return;

  // Real config: start from a clean slate so anything the tenant has since
  // removed (e.g. a background) doesn't linger from the cached theme.
  if (loaded) {
    THEME_VARS.forEach((name) => root.style.removeProperty(name));
    root.removeAttribute("data-font-color");
  }

  const remote = resolveRemoteBrandColor(config);

  const envColor = process.env.REACT_APP_THEME_COLOR || null;

  const brand = remote || envColor || DEFAULT_BRAND;
  applyBrandColor(brand);

  // Background — per-tenant override (only if provided and resolvable)
  const bg =
    config?.app_background ||
    config?.theme?.background ||
    process.env.REACT_APP_BG_COLOR;
  if (bg && isValidCssColor(bg)) {
    root.style.setProperty("--color-bg", bg);
  } else if (bg) {
    console.warn(`[theme] app_background "${bg}" isn't a valid CSS color (preset label instead of a hex/rgb value?) — keeping previous background.`);
  }

  // Font color — text color of accent-colored buttons, published from the
  // CMS theme builder. data-font-color switches on the rule in
  // design-system.css, so buttons keep their own color until one is set.
  const fontColor = config?.font_color;
  if (fontColor && isValidCssColor(fontColor)) {
    root.style.setProperty("--font-color", fontColor);
    root.setAttribute("data-font-color", "");
  } else if (fontColor) {
    console.warn(`[theme] font_color "${fontColor}" isn't a valid CSS color — keeping previous text color.`);
  }

  // Corner radius — one base value scales the whole --radius-* ramp
  // defined in design-system.css.
  const radius = config?.corner_radius;
  if (radius || radius === 0) root.style.setProperty("--radius-base", `${radius}px`);

  // Font family — swap the CSS variable everything inherits from; load
  // the web font itself if it isn't the bundled default.
  const font = config?.font_family;
  if (font && font !== "MyCustomFont") {
    loadGoogleFont(font);
    root.style.setProperty("--font-family", `"${font}", sans-serif`);
  }

  if (loaded) writeThemeCache(font && font !== "MyCustomFont" ? font : null);
}

export { DEFAULT_BRAND, isValidCssColor };
